import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Circle } from "lucide-react";
import { useOutletContext } from "react-router-dom";

import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import type { MeContext } from "./AppShell";

export function Overview() {
  const { me } = useOutletContext<MeContext>();
  const team = useQuery({ queryKey: ["team"], queryFn: api.team });

  const firstName = me?.user.name?.split(" ")[0] ?? "there";
  const memberCount = team.data?.members.length ?? 0;
  const PLAN_DISPLAY: Record<string, string> = {
    "all-access": "All-Access",
    free: "Free",
    blobship: "BlobShip",
  };
  const planLabel = PLAN_DISPLAY[me?.org.plan ?? ""] ?? me?.org.plan ?? "—";
  const toolCount = me?.entitlements.filter((e) => e.status !== "canceled")
    .length ?? 0;

  const stats = [
    { label: "TEAM", value: String(memberCount) },
    { label: "PLAN", value: planLabel },
    { label: "TOOLS", value: String(toolCount) },
  ];

  const checklist = [
    { done: true, label: "Create your OpsBelt account" },
    { done: toolCount > 0, label: "Subscribe to a tool" },
    { done: memberCount > 1, label: "Invite your team" },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">
          Good to see you, {firstName}.
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {me?.org.name} — everything on the belt, one account.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {stats.map((s) => (
          <Card key={s.label}>
            <CardContent className="p-5">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {s.label}
              </div>
              <div className="mt-1.5 font-display text-2xl font-bold">
                {s.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            Setup
          </div>
          <ul className="mt-3 space-y-2.5">
            {checklist.map((c) => (
              <li key={c.label} className="flex items-center gap-2.5 text-sm">
                {c.done ? (
                  <CheckCircle2 className="size-4 text-success" />
                ) : (
                  <Circle className="size-4 text-muted-foreground" />
                )}
                <span className={c.done ? "text-muted-foreground line-through" : ""}>
                  {c.label}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
